import { useState } from "react";
import { format, isValid, parse } from "date-fns";
import {
  Box,
  Chip,
  Divider,
  FormControlLabel,
  Grid,
  Paper,
  Switch,
  Tab,
  TabPanel,
  Tabs,
  TextField,
  TimePicker,
  Tooltip,
  Typography,
} from "../../../../components";
import { value } from "../../../../helpers";
import { formatIp } from "../../../../utils";

const diasSemana = [
  { sigla: "D", label: "Domingo" },
  { sigla: "S", label: "Segunda" },
  { sigla: "T", label: "Terça" },
  { sigla: "Q", label: "Quarta" },
  { sigla: "Q", label: "Quinta" },
  { sigla: "S", label: "Sexta" },
  { sigla: "S", label: "Sábado" },
];
const horarioPadrao = { entrada: "08:00", saida: "18:00" };

const formatHora = (hora) => (hora ? parse(hora, "HH:mm", new Date()) : null);

const ControleAcessoComponent = ({
  usuario,
  setUsuario,
  podeEditar,
  inputErros,
}) => {
  const [abaValue, setAbaValue] = useState(null);
  const horariosAcesso = usuario.horariosAcesso ?? [];
  const diasSelecionados = horariosAcesso.map((horario) => horario.diaSemana);
  const abaAtiva = diasSelecionados.includes(abaValue)
    ? abaValue
    : diasSelecionados[0] ?? false;

  const handleChangeControleAcesso = (event) => {
    setUsuario({
      ...usuario,
      controleAcesso: event.target.checked,
      horariosAcesso: horariosAcesso.length
        ? horariosAcesso
        : [1, 2, 3, 4, 5].map((diaSemana) => ({
            diaSemana,
            ...horarioPadrao,
          })),
    });
  };

  const handleSelecaoDia = (diaSemana) => {
    const horarios = diasSelecionados.includes(diaSemana)
      ? horariosAcesso.filter((horario) => horario.diaSemana !== diaSemana)
      : [...horariosAcesso, { diaSemana, ...horarioPadrao }].sort(
          (a, b) => a.diaSemana - b.diaSemana,
        );
    setUsuario({ ...usuario, horariosAcesso: horarios });
    setAbaValue(diaSemana);
  };

  const handleChangeHorario = (diaSemana, name, data) => {
    setUsuario({
      ...usuario,
      horariosAcesso: horariosAcesso.map((horario) =>
        horario.diaSemana === diaSemana
          ? { ...horario, [name]: isValid(data) ? format(data, "HH:mm") : null }
          : horario,
      ),
    });
  };

  return (
    <>
      <Divider sx={{ my: 3 }} />
      <Grid container spacing={2} className="formPadding align-items-center mb-3">
        <Grid item xs={6}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Configurações de Acesso
          </Typography>
        </Grid>
        <Grid item xs={6} className="d-flex justify-content-end">
          <FormControlLabel
            control={
              <Switch
                id="controleAcesso"
                name="controleAcesso"
                checked={usuario.controleAcesso ? true : false}
                onChange={handleChangeControleAcesso}
                disabled={!podeEditar}
                color="primary"
              />
            }
            label={usuario.controleAcesso ? "Ativado" : "Desativado"}
          />
        </Grid>
      </Grid>
      {usuario.controleAcesso && (
          <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1 }}>
            <Box sx={{ p: 2 }}>
              <Box className="d-flex align-items-center justify-content-between">
                <span>Dias da semana</span>
                <Box className="d-flex gap-2">
                  {diasSemana.map((dia, index) => {
                    const selecionado = diasSelecionados.includes(index);
                    return (
                      <Tooltip title={dia.label} key={index}>
                        <Chip
                          key={index}
                          label={dia.sigla}
                          clickable={podeEditar}
                          onClick={
                            podeEditar ? () => handleSelecaoDia(index) : undefined
                          }
                          color={selecionado ? "primary" : "default"}
                          variant={selecionado ? "filled" : "outlined"}
                          sx={{
                            width: 36,
                            height: 36,
                            borderRadius: "50%",
                            backgroundColor: selecionado ? undefined : "#fff",
                            "& .MuiChip-label": { px: 0 },
                          }}
                        />
                      </Tooltip>
                    );
                  })}
                </Box>
              </Box>
              <Paper sx={{ mt: 3 }}>
                <Tabs
                  value={abaAtiva}
                  onChange={(event, diaSemana) => setAbaValue(diaSemana)}
                  indicatorColor="primary"
                  textColor="primary"
                  variant="fullWidth"
                >
                  {horariosAcesso.map(({ diaSemana }) => (
                    <Tab
                      key={diaSemana}
                      value={diaSemana}
                      label={diasSemana[diaSemana].label}
                    />
                  ))}
                </Tabs>
              </Paper>
              {horariosAcesso.map((horario) => (
                <TabPanel
                  key={horario.diaSemana}
                  value={abaAtiva}
                  index={horario.diaSemana}
                >
                  <Grid container spacing={2} className="mt-2">
                    <Grid item xs={6}>
                      <TimePicker
                        id="entrada"
                        name="entrada"
                        label="Entrada"
                        format="HH:mm"
                        value={formatHora(horario.entrada)}
                        onChange={(data) =>
                          handleChangeHorario(
                            horario.diaSemana,
                            "entrada",
                            data,
                          )
                        }
                        disabled={!podeEditar}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TimePicker
                        id="saida"
                        name="saida"
                        label="Saída"
                        format="HH:mm"
                        value={formatHora(horario.saida)}
                        onChange={(data) =>
                          handleChangeHorario(horario.diaSemana, "saida", data)
                        }
                        disabled={!podeEditar}
                      />
                    </Grid>
                  </Grid>
                </TabPanel>
              ))}
              <TextField
                id="ipAcesso"
                name="ipAcesso"
                label="IP Fixo (Disponibilizado pela fornecedora de Internet)"
                variant="outlined"
                margin="normal"
                placeholder="000.000.000.000"
                value={value.text(usuario.ipAcesso)}
                onChange={(event) =>
                  setUsuario({
                    ...usuario,
                    ipAcesso: formatIp(event.target.value),
                  })
                }
                inputProps={{ maxLength: 15 }}
                fullWidth
                disabled={!podeEditar}
                error={inputErros.get("ipAcesso")}
              />
            </Box>
          </Box>
      )}
    </>
  );
};

export default ControleAcessoComponent;
